/**
 * Authentication calls (Phase 2).
 *
 * The tokens are never visible here. The server sets them as HttpOnly cookies, so this module
 * cannot read them and neither can any script the page loads — which is the point. Everything below
 * relies on `credentials: "include"` and on what the server says about the session, never on a
 * token the client is holding.
 *
 * The one thing the client *does* keep is `access_expires_in`, so it can refresh before a request
 * fails rather than discovering expiry as a 401 in the middle of reading an answer.
 */

import { ApiError } from "./client";
import type { Me, SessionInfo, AuthMode } from "./types";

const AUTH_BASE = "/api/auth";

async function unwrap<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let detail: unknown;
    try {
      detail = (await response.json())?.detail;
    } catch {
      /* a non-JSON error body; the status carries the meaning */
    }
    throw new ApiError(`Request failed with ${response.status}`, response.status, detail);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

function request(path: string, init: RequestInit = {}): Promise<Response> {
  return fetch(`${AUTH_BASE}${path}`, {
    ...init,
    headers: { Accept: "application/json", ...(init.headers ?? {}) },
    // Cookies are the whole mechanism; a same-origin default would still work behind the dev
    // proxy, but being explicit means a cross-origin deployment does not silently sign everyone
    // out.
    credentials: "include",
  });
}

/**
 * Which authentication mode the deployment runs.
 *
 * Asked rather than assumed: a password form against an IdP-backed deployment can only fail, and
 * the two modes are a deployment choice the frontend has no way to infer.
 */
export async function fetchAuthMode(): Promise<AuthMode> {
  return unwrap<AuthMode>(await request("/mode"));
}

export async function login(
  employeeId: string,
  password: string,
): Promise<SessionInfo> {
  return unwrap<SessionInfo>(
    await request("/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ employee_id: employeeId, password }),
    }),
  );
}

export async function refreshSession(): Promise<SessionInfo> {
  return unwrap<SessionInfo>(await request("/refresh", { method: "POST" }));
}

export async function logout(): Promise<void> {
  await unwrap<void>(await request("/logout", { method: "POST" }));
}

export async function fetchMe(): Promise<Me> {
  return unwrap<Me>(await request("/me"));
}

/** Fields a registrant supplies. The role is not among them — an administrator chooses it. */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<Me> {
  return unwrap<Me>(
    await request("/change-password", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    }),
  );
}

/** How long before expiry to refresh. Enough margin for a slow round trip on a busy branch line. */
export const REFRESH_MARGIN_SECONDS = 60;

/**
 * When to schedule the next refresh, in milliseconds.
 *
 * Clamped to a positive value: a server that returns a very short lifetime must not turn into a
 * refresh loop that hammers the endpoint, and a negative delay in `setTimeout` fires immediately.
 */
export function refreshDelayMs(accessExpiresIn: number): number {
  return Math.max(5, accessExpiresIn - REFRESH_MARGIN_SECONDS) * 1000;
}
